import { z } from "zod";
import { IanaTimezoneSchema } from "./timezone.schema";

const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format");

/** One day of slack absorbs the offset between the client timezone and the server clock. */
const FUTURE_DATE_TOLERANCE_DAYS = 1;

function maxAllowedDate(): string {
  const limit = new Date();
  limit.setUTCDate(limit.getUTCDate() + FUTURE_DATE_TOLERANCE_DAYS);
  return limit.toISOString().slice(0, 10);
}

export const DashboardMetricsQuerySchema = z
  .strictObject({
    dateFrom: IsoDateSchema,
    dateTo: IsoDateSchema,
    timezone: IanaTimezoneSchema,
  })
  .refine((value) => value.dateFrom <= value.dateTo, {
    message: "dateFrom must not be after dateTo",
    path: ["dateFrom"],
  })
  .refine((value) => value.dateTo <= maxAllowedDate(), {
    message: "dateTo must not be in the future",
    path: ["dateTo"],
  });

export const SpreadsheetMetricsSchema = z.object({
  totalFiles: z.number(),
  totalFileSize: z.number(),
  completed: z.number(),
  failed: z.number(),
  classificationCount: z.number(),
  extractionCount: z.number(),
  medianDurationMs: z.number(),
});

export const NonSpreadsheetMetricsSchema = SpreadsheetMetricsSchema.extend({
  totalPages: z.number(),
  totalCost: z.number(),
  avgCostPerDoc: z.number(),
  avgCostPerPage: z.number(),
  avgTokenUsagePerDoc: z.number(),
});

export const ExtractionDurationDistributionSchema = z.object({
  sampleCount: z.number().int().nonnegative(),
  avgDurationMs: z.number().nonnegative(),
  medianDurationMs: z.number().nonnegative(),
  stddevDurationMs: z.number().nonnegative(),
  minDurationMs: z.number().nonnegative(),
  maxDurationMs: z.number().nonnegative(),
});

export const TopRankedItemSchema = z.object({
  key: z.string(),
  name: z.string(),
  value: z.number().int().nonnegative(),
  sharePercentage: z.number().min(0).max(100),
  cost: z.number().nonnegative().optional(),
  fileCount: z.number().int().nonnegative().optional(),
});

export const TopRankedOthersSchema = z.object({
  value: z.number().int().nonnegative(),
  sharePercentage: z.number().min(0).max(100),
  cost: z.number().nonnegative().optional(),
  fileCount: z.number().int().nonnegative().optional(),
});

export const TopRankedListSchema = z.object({
  items: z.array(TopRankedItemSchema).max(5),
  others: TopRankedOthersSchema.nullable(),
  total: z.number().int().nonnegative(),
});

export type DashboardMetricsQuery = z.infer<typeof DashboardMetricsQuerySchema>;
export type SpreadsheetMetrics = z.infer<typeof SpreadsheetMetricsSchema>;
export type NonSpreadsheetMetrics = z.infer<typeof NonSpreadsheetMetricsSchema>;
export type ExtractionDurationDistribution = z.infer<typeof ExtractionDurationDistributionSchema>;
export type TopRankedItem = z.infer<typeof TopRankedItemSchema>;
export type TopRankedOthers = z.infer<typeof TopRankedOthersSchema>;
export type TopRankedList = z.infer<typeof TopRankedListSchema>;
